/**
 * Prévia controlável (itens 4.1 a 4.3): o seu atleta voa pelo estádio com a simulação de `packages/sim`, a 30
 * ticks por segundo e desenho interpolado, com a Pelota, os dois Petardos e a Faísca.
 * - Treino: os outros 13 atletas ficam parados no ar; os companheiros que recebem a Pelota devolvem o passe.
 * - Partida (item 4.3/4.4): as regras valem (início, relógio, placar, faltas, pênalti, morte súbita). Contra
 *   "parados" os outros 13 ficam no lugar e só passam a bola quando a recebem (o aceite do 4.4); contra
 *   "robôs" eles jogam com os robôs simples de `packages/sim` (os bots de verdade chegam no item 5.x).
 * Câmera do 4.4 em `camera.ts`; anel no chão sob cada atleta para mostrar onde ele está e a que altura.
 */
import * as THREE from "three";
import { CLIPS, type ClipId } from "@besenliga/anim";
import { loadModels, type AthleteSpec } from "@besenliga/athlete-three";
import { randomAppearance } from "@besenliga/content";
import {
  ABILITIES,
  ABILITY_LABELS,
  ABILITY_OF,
  EXAMPLE_KITS,
  matchRating,
  FLIGHT,
  MATCH,
  NET,
  STATS,
  seedRng,
  statCaps,
  type FlightStats,
  type GraphicsSettings,
  type Kit,
  type Position,
} from "@besenliga/shared";
import {
  BUTTON,
  FixedStepClock,
  Simulation,
  TICK_SECONDS,
  type AthleteState,
  robotInputs,
  type AthleteMatchStats,
  type InputCommand,
  type MatchState,
  emptyStats,
  type PlayEvent,
  type Team,
  type WorldState,
} from "@besenliga/sim";
import {
  SUN_DIRECTION,
  addStadiumLights,
  buildStadium,
  followSun,
  obstacleHeight,
  type Stadium,
} from "@besenliga/stadium-three";
import {
  BlobShadows,
  animateRider,
  createRider,
  playAction,
  updateRiderLod,
  type Rider,
} from "../stadium/riders";
import { Balls3D } from "./balls3d";
import { CAMERA, cameraOffset, clearDistance, easeDistance } from "./camera";
import { Controls, QUICK_CHAT, commandFrom } from "./controls";
import { HeightRings } from "./rings";

export const PLAYER_ID = "jogador";
/** Na prévia a Faísca aparece cedo, para dar para testar a captura sem esperar 3 minutos. */
export const PREVIEW_SPARK_SECONDS = 15;

export type PreviewMode = "treino" | "parados" | "robos";

/** Quem você controla: aparência e corpo do atleta salvo no criador, ou um atleta padrão. */
/** Os dois times em campo: nome curto (placar, jogadas) e uniforme. O seu é sempre a casa. */
export type Clubs = Record<Team, { name: string; kit: Kit }>;

export const DEFAULT_CLUBS: Clubs = {
  casa: { name: "Corujas", kit: EXAMPLE_KITS["corujas-campinas"] },
  fora: { name: "Lobos", kit: EXAMPLE_KITS["lobos-guaiba"] },
};

/** Uma linha da tela de resultado. */
export interface ResultLine {
  id: string;
  team: Team;
  role: Position;
  you: boolean;
  stats: AthleteMatchStats;
  rating: number;
}

export interface PilotSetup {
  spec: AthleteSpec;
  kit: Kit;
  role: Position;
}

type Other = { pos: Position; at: [number, number, number]; team: Team };

/** Os dois times completos (7 × 7) nas posições do treino (casa no lado x < 0). */
const LINEUP: Other[] = [
  { pos: "goleiro", at: [-50, 15, 0], team: "casa" },
  { pos: "artilheiro", at: [-20, 10, -2], team: "casa" },
  { pos: "artilheiro", at: [-12, 9, -12], team: "casa" },
  { pos: "artilheiro", at: [-4, 11, 14], team: "casa" },
  { pos: "batedor", at: [-24, 12, -16], team: "casa" },
  { pos: "batedor", at: [-24, 12, 16], team: "casa" },
  { pos: "cacador", at: [-34, 22, 4], team: "casa" },
  { pos: "goleiro", at: [50, 15, 0], team: "fora" },
  { pos: "artilheiro", at: [14, 9, -12], team: "fora" },
  { pos: "artilheiro", at: [22, 10, 2], team: "fora" },
  { pos: "artilheiro", at: [14, 9, 14], team: "fora" },
  { pos: "batedor", at: [26, 12, -16], team: "fora" },
  { pos: "batedor", at: [26, 12, 16], team: "fora" },
  { pos: "cacador", at: [34, 22, -4], team: "fora" },
];
const parkedId = (i: number) => `parado-${String(i).padStart(2, "0")}`;

/** Os outros 13: o seu time sem uma vaga da sua posição, e o adversário completo. */
export function othersFor(role: Position): Other[] {
  const mine = LINEUP.findIndex((o) => o.team === "casa" && o.pos === role);
  return LINEUP.filter((_, i) => i !== mine);
}

/** Atributos a partir do corpo: o teto de cada atributo menos uma margem de evolução. */
export function pilotStats(spec: AthleteSpec): FlightStats {
  const caps = statCaps({ sex: spec.sexo, height: spec.altura, build: spec.biotipo });
  const stats = {} as FlightStats;
  for (const s of STATS) stats[s] = caps[s] - 15;
  return stats;
}

/** Clipe de animação de cada jogada, para quem a fez. */
const PLAY_CLIP: Partial<Record<PlayEvent["kind"], ClipId>> = {
  passe: "passe",
  arremesso: "arremesso",
  rebatida: "rebatida",
  defesa: "defesa",
  posse: "agarrar",
  captura: "mergulho",
  atordoado: "atordoado",
  queda: "atordoado",
};

/** Clipe da habilidade de cada posição (a Finta Espiral é um giro da vassoura, feito em `tick`). */
const ABILITY_CLIP: Record<Position, ClipId | null> = {
  artilheiro: null,
  goleiro: "defesa",
  batedor: "rebatida",
  cacador: "mergulho",
};

export interface FlightHud {
  stamina: number;
  exhausted: boolean;
  turbo: boolean;
  /** km/h */
  speed: number;
  height: number;
  role: Position;
  hasBall: boolean;
  /** 0 a 1 enquanto carrega o arremesso. */
  charge: number;
  stunned: boolean;
  score: Record<Team, number>;
  /** Últimas jogadas, da mais nova para a mais velha. */
  feed: { id: number; text: string; at: number }[];
  sparkActive: boolean;
  /** Habilidade: 0 a 1 de recarga (1 = pronta) e se está valendo agora. */
  ability: { ready: number; active: boolean };
  /** Regras da partida de teste (null no treino). */
  match:
    | (Pick<
        MatchState,
        "phase" | "phaseTicks" | "clockTicks" | "suddenDeath" | "suddenDeathTicks" | "winner" | "endReason"
      > & {
        penaltyTeam: Team | null;
      })
    | null;
  time: number;
  fps: number;
}

const angleLerp = (a: number, b: number, t: number) => {
  let d = b - a;
  while (d > Math.PI) d -= Math.PI * 2;
  while (d < -Math.PI) d += Math.PI * 2;
  return a + d * t;
};
const yawOf = (h: { x: number; z: number }) => Math.atan2(h.x, h.z);
const FINTA_TICKS = Math.round(ABILITIES.finta.seconds * NET.simTickHz);
/** Quanto tempo um companheiro segura a bola antes de devolver o passe (ticks). */
const RETURN_PASS_TICKS = 40;

export class FlightView {
  private readonly renderer: THREE.WebGLRenderer;
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.PerspectiveCamera(CAMERA.fov, 1, 0.5, 2000);
  /** Distância escolhida na roda do mouse e a distância atual (encurta perto de torres). */
  private zoom: number = CAMERA.distance;
  private camDistance: number = CAMERA.distance;
  private readonly rings = new HeightRings(14);
  private readonly stadium: Stadium;
  private readonly sun: THREE.DirectionalLight;
  private readonly sim: Simulation;
  private readonly clock = new FixedStepClock();
  private readonly controls: Controls;
  private prev: WorldState;
  private readonly riders = new Map<string, Rider>();
  private readonly balls3d = new Balls3D();
  private blobs: BlobShadows | null = null;
  private readonly target = new THREE.Vector3();
  private readonly hand = new THREE.Vector3();
  private readonly names = new Map<string, string>();
  private feedId = 0;
  private camTheta = Math.PI;
  /** Direção da câmera travada para o direcional atual (ver `tick`). */
  private latchTheta = Math.PI;
  private dirKey = "";
  private heldTicks = 0;
  private frame = 0;
  private last = 0;
  private time = 0;
  private fpsFrames = 0;
  private fpsSince = 0;
  private readonly resizeObserver: ResizeObserver;
  /** Na partida de teste, todos menos você jogam com os robôs simples. */
  private readonly others: Other[];
  private readonly robots: Set<string>;
  /** Botões do seu último comando (para os parados saberem que você pediu a bola). */
  private myButtons = 0;
  readonly hud: FlightHud;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly settings: GraphicsSettings,
    private readonly pilot: PilotSetup,
    private readonly mode: PreviewMode = "treino",
    private readonly clubs: Clubs = DEFAULT_CLUBS,
  ) {
    this.others = othersFor(pilot.role);
    this.robots = new Set(this.others.map((_, i) => parkedId(i)));
    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: settings.antialias,
      powerPreference: "high-performance",
    });
    this.renderer.setPixelRatio(
      Math.min(window.devicePixelRatio, settings.maxPixelRatio) * settings.resolutionScale,
    );
    this.renderer.shadowMap.enabled = settings.shadowMapSize > 0;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.sun = addStadiumLights(this.scene, { shadowSize: settings.shadowMapSize });
    this.stadium = buildStadium({
      home: clubs.casa.kit,
      away: clubs.fora.kit,
      anisotropy: this.renderer.capabilities.getMaxAnisotropy(),
      animatedCrowd: settings.animatedCrowd,
      occupancy: settings.crowdOccupancy,
      trees: settings.trees,
    });
    this.scene.add(this.stadium.root, this.balls3d.root, this.rings.mesh);
    canvas.addEventListener("wheel", this.onWheel, { passive: false });
    this.stadium.bakeShadows(this.renderer, SUN_DIRECTION, settings.bakeSize);
    this.controls = new Controls(window, canvas);

    this.sim = new Simulation({
      seed: 41,
      ...(mode !== "treino"
        ? { mode: "partida" as const }
        : { sparkAtTick: PREVIEW_SPARK_SECONDS * NET.simTickHz }),
      athletes: [
        {
          id: PLAYER_ID,
          team: "casa",
          role: pilot.role,
          position: { x: -30, y: 10, z: -20 },
          stats: pilotStats(pilot.spec),
        },
        ...this.others.map((p, i) => ({
          id: parkedId(i),
          team: p.team,
          role: p.pos,
          position: { x: p.at[0], y: p.at[1], z: p.at[2] },
        })),
      ],
    });
    this.prev = this.sim.state;
    const me = this.athlete(this.sim.state, PLAYER_ID);
    this.camTheta = yawOf(me.heading) + Math.PI;
    this.names.set(PLAYER_ID, "Você");
    this.hud = {
      stamina: 1,
      exhausted: false,
      turbo: false,
      speed: 0,
      height: 0,
      role: pilot.role,
      hasBall: false,
      charge: 0,
      stunned: false,
      score: { casa: 0, fora: 0 },
      feed: [],
      sparkActive: false,
      ability: { ready: 1, active: false },
      match: null,
      time: 0,
      fps: 0,
    };

    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(canvas);
    this.resize();
    this.frame = requestAnimationFrame(this.tick);
  }

  async loadAthletes(modelsUrl: string): Promise<void> {
    const models = await loadModels(modelsUrl);
    this.addRider(
      PLAYER_ID,
      createRider(models, { ...this.pilot.spec, posicao: this.pilot.role }, this.pilot.kit, this.settings),
    );
    this.others.forEach((p, i) => {
      const sexo = i % 2 === 0 ? "homem" : "mulher";
      const [, look] = randomAppearance(seedRng(500 + i), sexo);
      const spec: AthleteSpec = { sexo, altura: "medio", biotipo: "medio", posicao: p.pos, ...look };
      const kit = this.clubs[p.team].kit;
      this.addRider(parkedId(i), createRider(models, spec, kit, this.settings));
    });
    if (this.settings.shadowMapSize === 0) {
      this.blobs = new BlobShadows(this.riders.size);
      this.scene.add(this.blobs.mesh);
    }
  }

  /** Estado atual da simulação (para testes e para o painel). */
  get state(): Readonly<WorldState> {
    return this.sim.state;
  }

  /** Pausa a simulação (menu de pausa); a cena continua desenhando. */
  paused = false;

  /** Linhas da tela de resultado: estatísticas e nota de cada atleta (null fora de partida). */
  results(): ResultLine[] | null {
    const m = this.sim.state.match;
    if (!m) return null;
    return this.sim.state.athletes.map((a) => {
      const stats = m.stats[a.id] ?? emptyStats();
      const other: Team = a.team === "casa" ? "fora" : "casa";
      const result =
        m.winner === "empate" || !m.winner ? "empate" : m.winner === a.team ? "vitoria" : "derrota";
      return {
        id: a.id,
        team: a.team,
        role: a.role,
        you: a.id === PLAYER_ID,
        stats,
        rating: matchRating(a.role, stats, { result, conceded: m.goals[other] }),
      };
    });
  }

  dispose(): void {
    cancelAnimationFrame(this.frame);
    this.canvas.removeEventListener("wheel", this.onWheel);
    this.rings.dispose();
    this.resizeObserver.disconnect();
    this.controls.dispose();
    this.blobs?.dispose();
    this.balls3d.dispose();
    this.stadium.dispose();
    this.renderer.dispose();
    this.renderer.forceContextLoss();
  }

  /** Roda do mouse: aproxima ou afasta a câmera, dentro da faixa. */
  private readonly onWheel = (e: WheelEvent): void => {
    e.preventDefault();
    this.zoom = Math.min(CAMERA.maxDistance, Math.max(CAMERA.minDistance, this.zoom + e.deltaY * 0.02));
  };

  /** Chat rápido: por enquanto só aparece para você; no online (6.x) vai para o time. */
  private say(text: string): void {
    if (!text) return;
    this.hud.feed = [{ id: ++this.feedId, text: `Você: ${text}`, at: this.time }, ...this.hud.feed].slice(
      0,
      4,
    );
  }

  private addRider(id: string, r: Rider): void {
    this.riders.set(id, r);
    this.scene.add(r.holder);
  }

  private athlete(w: WorldState, id: string): AthleteState {
    const a = w.athletes.find((x) => x.id === id);
    if (!a) throw new Error(`Atleta ${id} não está na partida`);
    return a;
  }

  private resize(): void {
    const { clientWidth: w, clientHeight: h } = this.canvas;
    if (!w || !h) return;
    this.renderer.setSize(w, h, false);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
  }

  /** Comandos dos atletas parados: só quem está com a Pelota se mexe, vira para você e devolve o passe. */
  private parkedCommands(w: WorldState): Record<string, InputCommand> {
    const frame: Record<string, InputCommand> = {};
    const holder = w.balls?.pelota.holder;
    if (!holder || holder === PLAYER_ID) {
      this.heldTicks = 0;
      return frame;
    }
    this.heldTicks++;
    const a = this.athlete(w, holder);
    const me = this.athlete(w, PLAYER_ID);
    const mate = a.team === me.team;
    // Você pediu a bola (clique direito / B): o companheiro passa na hora.
    const called = mate && (this.myButtons & BUTTON.call) !== 0 && this.heldTicks > 3;
    const goal = mate ? me.position : { x: 0, y: 12, z: 0 };
    const dx = goal.x - a.position.x;
    const dz = goal.z - a.position.z;
    const l = Math.sqrt(dx * dx + dz * dz) || 1;
    // Um toque leve no direcional só para a vassoura virar.
    frame[holder] = {
      x: Math.round((dx / l) * 20),
      y: 0,
      z: Math.round((dz / l) * 20),
      buttons: this.heldTicks === RETURN_PASS_TICKS || called ? BUTTON.action : 0,
    };
    return frame;
  }

  private onPlays(plays: readonly PlayEvent[]): void {
    for (const p of plays) {
      const clip = PLAY_CLIP[p.kind];
      const r = this.riders.get(p.athlete);
      if (clip && r) playAction(r, CLIPS[clip], this.time);
      if (!this.sim.state.match) {
        if (p.kind === "gol" && p.team) this.hud.score[p.team] += 10;
        if (p.kind === "captura" && p.team) this.hud.score[p.team] += 30;
      }
      if (p.kind === "habilidade") {
        const a = this.sim.state.athletes.find((x) => x.id === p.athlete);
        const ac = a ? ABILITY_CLIP[a.role] : null;
        if (ac && r) playAction(r, CLIPS[ac], this.time);
      }
      if (p.kind === "gol" && p.athlete) {
        const scorer = this.riders.get(p.athlete);
        if (scorer) playAction(scorer, CLIPS.comemorar, this.time + 0.3);
      }
      const text = this.describe(p);
      if (text) this.hud.feed = [{ id: ++this.feedId, text, at: this.time }, ...this.hud.feed].slice(0, 4);
    }
  }

  private who(id: string | undefined): string {
    if (!id) return "";
    if (id === PLAYER_ID) return "Você";
    const a = this.sim.state.athletes.find((x) => x.id === id);
    if (!a) return id;
    const team = this.clubs[a.team].name;
    const role = { artilheiro: "artilheiro", goleiro: "goleiro", batedor: "batedor", cacador: "caçador" }[
      a.role
    ];
    return `${role} dos ${team}`;
  }

  private describe(p: PlayEvent): string | null {
    const me = p.athlete === PLAYER_ID;
    switch (p.kind) {
      case "gol":
        return `Gol dos ${p.team ? this.clubs[p.team].name : ""}! +10`;
      case "passe":
        return me ? `Você passou para o ${this.who(p.other)}` : null;
      case "arremesso":
        return me ? "Você arremessou" : null;
      case "posse":
        return me ? "A Pelota é sua" : null;
      case "roubo":
        return me ? `O ${this.who(p.other)} tomou a Pelota de você` : null;
      case "defesa":
        return `Defesa do ${this.who(p.athlete)}`;
      case "rebatida":
        return me
          ? p.other
            ? `Você rebateu o Petardo no ${this.who(p.other)}`
            : "Você rebateu o Petardo"
          : null;
      case "atordoado":
        return me ? "Você foi atingido por um Petardo" : `${this.who(p.athlete)} atordoado`;
      case "falta": {
        const kind = {
          empurrao: "empurrão",
          bloqueio: "bloqueio do aro",
          "petardo-goleiro": "Petardo no goleiro",
        }[p.foul ?? "empurrao"];
        return `Falta (${kind}) ${me ? "sua" : `do ${this.who(p.athlete)}`}`;
      }
      case "penalti":
        return `Pênalti para os ${p.team ? this.clubs[p.team].name : ""}`;
      case "morte-subita":
        return "Empate no tempo: morte súbita!";
      case "habilidade": {
        const a = this.sim.state.athletes.find((x) => x.id === p.athlete);
        const name = a ? ABILITY_LABELS[ABILITY_OF[a.role]].name : "Habilidade";
        return me ? `Você usou ${name}` : null;
      }
      case "queda":
        return me ? "Mergulho baixo demais: você caiu!" : `${this.who(p.athlete)} caiu do mergulho`;
      case "aparece":
        return "A Faísca apareceu!";
      case "captura":
        return `${me ? "Você capturou" : `O ${this.who(p.athlete)} capturou`} a Faísca! +30`;
      default:
        return null;
    }
  }

  private readonly tick = (nowMs: number): void => {
    this.frame = requestAnimationFrame(this.tick);
    const now = nowMs / 1000;
    const dt = this.last ? Math.min(0.1, now - this.last) : 0;
    this.last = now;
    this.time += dt;

    // Simulação. O direcional é relativo à câmera. Para trás, a direção da câmera fica travada enquanto o
    // jogador mantém o direcional: segurar "para trás" faz uma meia-volta suave e termina voando para lá, em
    // vez de girar sem fim atrás de uma câmera que acompanha a vassoura. Para a frente e para os lados a
    // câmera vale ao vivo, então segurar A ou D faz uma curva contínua.
    const ticks = this.paused ? 0 : this.clock.advance(dt);
    for (let i = 0; i < ticks; i++) {
      this.prev = this.sim.state;
      const holding = this.prev.balls?.pelota.holder === PLAYER_ID;
      const intent = this.controls.intent(holding);
      const dirKey = `${Math.round(intent.forward * 4)},${Math.round(intent.right * 4)}`;
      if (dirKey !== this.dirKey) {
        this.dirKey = dirKey;
        this.latchTheta = this.camTheta;
      }
      const theta = intent.forward < -0.1 ? this.latchTheta : this.camTheta;
      const camForward = { x: -Math.sin(theta), z: -Math.cos(theta) };
      const frame =
        this.mode === "robos" ? { ...robotInputs(this.prev, this.robots) } : this.parkedCommands(this.prev);
      // "Passa!" no chat rápido também pede a bola.
      if (intent.chat === 0) intent.call = true;
      frame[PLAYER_ID] = commandFrom(intent, camForward);
      this.myButtons = frame[PLAYER_ID].buttons;
      if (intent.chat !== undefined) this.say(QUICK_CHAT[intent.chat] ?? "");
      this.sim.step(frame);
      this.onPlays(this.sim.state.plays);
    }

    // Desenho: interpola entre o tick anterior e o atual.
    const alpha = this.clock.alpha;
    const cur = this.sim.state;
    for (const a of cur.athletes) {
      const r = this.riders.get(a.id);
      if (!r) continue;
      const p = this.prev.athletes.find((x) => x.id === a.id) ?? a;
      r.holder.position.set(
        p.position.x + (a.position.x - p.position.x) * alpha,
        p.position.y + (a.position.y - p.position.y) * alpha,
        p.position.z + (a.position.z - p.position.z) * alpha,
      );
      const y0 = yawOf(p.heading);
      const y1 = yawOf(a.heading);
      const yaw = angleLerp(y0, y1, alpha);
      const turnRate = angleLerp(0, y1 - y0, 1) / TICK_SECONDS;
      const speed = Math.sqrt(a.velocity.x * a.velocity.x + a.velocity.z * a.velocity.z);
      // Aceleração na direção da vassoura: positiva arrancando, negativa freando.
      const along = a.velocity.x * a.heading.x + a.velocity.z * a.heading.z;
      const alongBefore = p.velocity.x * a.heading.x + p.velocity.z * a.heading.z;
      const accel = (along - alongBefore) / TICK_SECONDS;
      // Finta Espiral: um giro completo da vassoura enquanto dura.
      const finta = ABILITY_OF[a.role] === "finta" && a.abilityTicks > 0;
      const spin = finta ? (1 - (a.abilityTicks - alpha) / FINTA_TICKS) * Math.PI * 2 : 0;
      animateRider(
        r,
        this.time,
        yaw,
        { turnRate, climb: a.velocity.y, speed, turbo: a.turbo, accel, spin },
        dt,
      );
    }
    this.blobs?.update([...this.riders.values()]);
    this.rings.update(
      cur.athletes.flatMap((a) => {
        const r = this.riders.get(a.id);
        return r ? [{ position: r.holder.position, team: a.team, mine: a.id === PLAYER_ID }] : [];
      }),
    );

    // Pelota na mão de quem carrega.
    let held: THREE.Vector3 | undefined;
    const holderId = cur.balls?.pelota.holder;
    const handBone = holderId ? this.riders.get(holderId)?.rig.bones["hand_r"] : undefined;
    if (handBone) held = handBone.getWorldPosition(this.hand);
    if (cur.balls && this.prev.balls) this.balls3d.update(this.prev.balls, cur.balls, alpha, this.time, held);

    // Câmera atrás e acima do seu atleta, virando com atraso para a direção da vassoura.
    const me = this.athlete(cur, PLAYER_ID);
    const mine = this.riders.get(PLAYER_ID);
    const focus = mine
      ? mine.holder.position
      : new THREE.Vector3(me.position.x, me.position.y, me.position.z);
    const meSpeed = Math.sqrt(me.velocity.x * me.velocity.x + me.velocity.z * me.velocity.z);
    this.camTheta = angleLerp(this.camTheta, yawOf(me.heading) + Math.PI, 1 - Math.exp(-CAMERA.lag * dt));
    this.target.lerp(focus, dt > 0 ? 1 - Math.exp(-10 * dt) : 1);
    // Atrás e acima, a 55°; mais perto quando uma torre ficaria entre a câmera e você.
    const wanted = clearDistance(this.target, this.camTheta, this.zoom, obstacleHeight);
    // Para dentro na hora (nunca atravessa a torre); para fora, devagar.
    this.camDistance = Math.min(easeDistance(this.camDistance, wanted, dt), wanted);
    const off = cameraOffset(this.camTheta, this.camDistance);
    this.camera.position.set(this.target.x + off.x, this.target.y + off.y, this.target.z + off.z);
    this.camera.lookAt(this.target.x, this.target.y + 2, this.target.z);
    followSun(this.sun, this.target, 45);
    for (const r of this.riders.values()) updateRiderLod(r, this.camera, this.settings);
    this.stadium.update(this.time);
    this.renderer.render(this.scene, this.camera);

    this.hud.stamina = me.stamina;
    this.hud.exhausted = me.exhausted;
    this.hud.turbo = me.turbo;
    this.hud.speed = Math.round(meSpeed * 3.6);
    this.hud.height = Math.round(me.position.y - FLIGHT.minHeight);
    this.hud.hasBall = holderId === PLAYER_ID;
    this.hud.charge = Math.min(1, me.chargeTicks / NET.simTickHz);
    this.hud.stunned = me.stunTicks > 0;
    this.hud.sparkActive = cur.balls?.faisca.active ?? false;
    this.hud.ability = {
      ready: 1 - me.abilityCooldown / (MATCH.abilityCooldownSeconds * NET.simTickHz),
      active: me.abilityTicks > 0,
    };
    const m = cur.match;
    if (m) {
      this.hud.score = { ...m.score };
      this.hud.match = {
        phase: m.phase,
        phaseTicks: m.phaseTicks,
        clockTicks: m.clockTicks,
        suddenDeath: m.suddenDeath,
        suddenDeathTicks: m.suddenDeathTicks,
        winner: m.winner,
        endReason: m.endReason,
        penaltyTeam: m.penalty?.team ?? null,
      };
    }
    this.hud.time = this.time;
    this.fpsFrames++;
    if (now - this.fpsSince >= 1) {
      this.hud.fps = Math.round(this.fpsFrames / (now - this.fpsSince));
      this.fpsFrames = 0;
      this.fpsSince = now;
    }
  };
}
